"use client";

import { useCallback, useEffect, useState } from "react";
import { useTranslations } from "next-intl";
import { X } from "lucide-react";
import posthog from "posthog-js";
import {
	CAMPAIGN_COOKIE_MAX_AGE,
	CAMPAIGN_COOKIE_NAME,
	CAMPAIGN_DISMISSED_COOKIE_NAME,
	CAMPAIGN_KILL_PARAM,
	getQrCampaign,
	parseCampaignCookie,
	type QrCampaign,
	type QrCampaignKey,
} from "@/lib/campaigns/qr-campaigns";

type ActiveCampaign = { key: QrCampaignKey; campaign: QrCampaign };

function readCookie(name: string): string | null {
	if (typeof document === "undefined") return null;
	const raw = document.cookie
		.split("; ")
		.find((entry) => entry.startsWith(`${name}=`))
		?.slice(name.length + 1);
	if (raw === undefined) return null;
	try {
		return decodeURIComponent(raw);
	} catch {
		return raw;
	}
}

function writeDismissedCookie(key: QrCampaignKey) {
	const secure = typeof location !== "undefined" && location.protocol === "https:";
	document.cookie = `${CAMPAIGN_DISMISSED_COOKIE_NAME}=${key}; path=/; max-age=${CAMPAIGN_COOKIE_MAX_AGE}; samesite=lax${
		secure ? "; secure" : ""
	}`;
}

/**
 * Resolves the ribbon state from the browser only. The cookie payload is NOT
 * trusted: only `key` is read, looked up in the registry, and the voucher code
 * comes from the registry entry. A `code` field in the cookie is ignored.
 */
function readActiveCampaign(): ActiveCampaign | null {
	if (typeof window === "undefined") return null;
	if (new URLSearchParams(window.location.search).get(CAMPAIGN_KILL_PARAM) === "0") return null;
	const parsed = parseCampaignCookie(readCookie(CAMPAIGN_COOKIE_NAME));
	if (!parsed) return null;
	// null for an unknown key AND for `enabled: false` - one guard covers both.
	const campaign = getQrCampaign(parsed.key);
	if (!campaign) return null;
	// Compare against parsed.key, not mere presence: a dismissal recorded for a
	// DIFFERENT campaign (bb_campaign_dismissed=<other-key>) must not suppress
	// this one. With a single registered campaign this was correct by accident.
	if (readCookie(CAMPAIGN_DISMISSED_COOKIE_NAME) === parsed.key) return null;
	return { key: parsed.key, campaign };
}

/**
 * Storefront ribbon for an active QR campaign (design §3.6). The QR beacon shows
 * only the code image and the discount rate, so this is the first place the
 * visitor learns the voucher code and its condition.
 *
 * The cookie is read exclusively inside an effect: on the very first request it
 * arrives via `Set-Cookie` and does not exist during the server render, so a
 * read while rendering would be a hydration mismatch.
 */
export function CampaignRibbon() {
	const t = useTranslations("campaignBanner");
	const [active, setActive] = useState<ActiveCampaign | null>(null);
	const [hidden, setHidden] = useState(false);

	const shouldRender = active !== null && !hidden;
	const campaignKey = active?.key ?? null;

	useEffect(() => {
		// eslint-disable-next-line react-hooks/set-state-in-effect -- hydration-safe cookie read: `document.cookie` does not exist on the server, so this cannot become a lazy useState initialiser; the server render and the first client render must both show nothing, and the cookie value fills in right after
		setActive(readActiveCampaign());
	}, []);

	useEffect(() => {
		if (!shouldRender || !campaignKey) return;
		posthog.capture("campaign_ribbon_shown", { campaign: campaignKey });
	}, [shouldRender, campaignKey]);

	const handleDismiss = useCallback(() => {
		if (!campaignKey) return;
		writeDismissedCookie(campaignKey);
		posthog.capture("campaign_ribbon_dismissed", { campaign: campaignKey });
		setHidden(true);
	}, [campaignKey]);

	if (!shouldRender || !active) return null;

	return (
		<div
			role="region"
			aria-label="Campaign discount"
			className="flex w-full items-center gap-3 border-b border-border bg-primary px-4 py-2.5 text-sm text-primary-foreground sm:px-6"
		>
			<span className="text-base" aria-hidden>
				🎁
			</span>
			<p className="flex-1 leading-snug">
				<span className="font-semibold">{t("headline", { percent: active.campaign.discountPercent })}</span>{" "}
				<span>{t("body", { code: active.campaign.voucherCode })}</span>
			</p>
			<button
				type="button"
				onClick={handleDismiss}
				aria-label={t("dismissAria")}
				className="hover:bg-primary-foreground/15 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-lg"
			>
				<X className="h-4 w-4" />
			</button>
		</div>
	);
}
